import { LitElement, css, html, svg } from 'lit';

/** Original small pixel-spider motif. Decorative unless `label` is set. */
export const spiderSvg = (size = 24) => svg`
  <svg width=${size} height=${size} viewBox="0 0 16 16" shape-rendering="crispEdges" aria-hidden="true">
    <g fill="currentColor">
      <rect x="6" y="5" width="4" height="3"/><rect x="5" y="8" width="6" height="4"/>
      <rect x="2" y="4" width="1" height="3"/><rect x="3" y="7" width="2" height="1"/>
      <rect x="13" y="4" width="1" height="3"/><rect x="11" y="7" width="2" height="1"/>
      <rect x="1" y="9" width="1" height="3"/><rect x="2" y="9" width="3" height="1"/>
      <rect x="14" y="9" width="1" height="3"/><rect x="11" y="9" width="3" height="1"/>
      <rect x="3" y="12" width="1" height="2"/><rect x="4" y="11" width="1" height="1"/>
      <rect x="12" y="12" width="1" height="2"/><rect x="11" y="11" width="1" height="1"/>
    </g>
    <rect x="6" y="6" width="1" height="1" fill="#e2594f"/><rect x="9" y="6" width="1" height="1" fill="#e2594f"/>
  </svg>`;

/** Subtle corner cobweb for panels. Place inside a positioned container. */
export const webCorner = svg`
  <svg class="web" width="48" height="48" viewBox="0 0 48 48" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1" opacity="0.25">
    <path d="M0 0 L48 48 M0 0 L48 20 M0 0 L20 48"/>
    <path d="M14 0 Q12 12 0 14 M28 0 Q24 24 0 28 M42 0 Q36 36 0 42"/>
  </svg>`;

const STORAGE_KEY = 'ka.secrets.v1';

export interface SecretProgress {
  version: number;
  found: string[];
}

export function readProgress(version: number): SecretProgress {
  try {
    const p = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null') as SecretProgress | null;
    if (p && p.version === version && Array.isArray(p.found)) return p;
  } catch {
    /* private mode or corrupted: start fresh */
  }
  return { version, found: [] };
}

export function writeProgress(p: SecretProgress): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    /* progress is a nicety; ignore storage failures */
  }
  window.dispatchEvent(new CustomEvent('ka-secret-progress', { detail: p }));
}

export function resetProgress(version: number): void {
  writeProgress({ version, found: [] });
}

/**
 * A hidden spider symbol. Clicking it records the find locally.
 * This is a playful puzzle, not access control: never hide unpublished content behind it.
 */
export class KaSpiderSymbol extends LitElement {
  static override properties = { symbol: {}, version: { type: Number }, found: { state: true } };
  declare symbol: string;
  declare version: number;
  declare found: boolean;

  static override styles = css`
    :host {
      display: inline-block;
    }
    button {
      all: unset;
      cursor: pointer;
      min-width: 44px;
      min-height: 44px;
      display: grid;
      place-items: center;
      color: var(--ka-stone-600);
      transition: color 0.3s;
    }
    button:hover,
    button:focus-visible {
      color: var(--ka-red);
    }
    button:focus-visible {
      outline: 3px solid var(--ka-focus);
    }
    :host([found]) button {
      color: var(--ka-red-bright);
    }
  `;

  constructor() {
    super();
    this.symbol = '';
    this.version = 1;
    this.found = false;
  }

  override connectedCallback() {
    super.connectedCallback();
    this.found = readProgress(this.version).found.includes(this.symbol);
    this.toggleAttribute('found', this.found);
  }

  private onClick() {
    const p = readProgress(this.version);
    if (!p.found.includes(this.symbol)) p.found.push(this.symbol);
    writeProgress(p);
    this.found = true;
    this.toggleAttribute('found', true);
  }

  override render() {
    return html`<button
      @click=${this.onClick}
      aria-label=${this.found ? 'Spider symbol (found)' : 'A small spider symbol'}
      aria-pressed=${this.found ? 'true' : 'false'}
    >
      ${spiderSvg(18)}
    </button>`;
  }
}
customElements.define('ka-spider-symbol', KaSpiderSymbol);
